import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "../css/bg.css";
import Logo from "../Components/Assets/logo.svg";
import segLogo from "../Components/Assets/seg-logo.png";
import Market from "../Components/market/market";
import HeroImage from "../Components/Assets/hero.jpg";
import Background from '../Components/background/background';
import NextBtnLoader from '../Components/loader/loader';
import DOBPickerInput from "../Components/dobPicker/dobPicker";
import axios from 'axios';
import config from '../Context/Config';


const LoginPage = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [showOTP, setShowOTP] = useState(false);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [dob, setDob] = useState("");
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tokenParam = params.get("token");
    config.token = tokenParam;
    if (!tokenParam) {
      navigate("/context-not-found", { state: { openedViaButton: true } });
    }
    else {
      const verifyToken = async () => {
        try {
          setIsLoading(true);
          const response = await axios.post(
            `${config.API_BASE_URL}/verify_token`, // Replace with your Lambda endpoint
            { token: tokenParam }
          );

          const { is_valid, form_submitted } = response.data;

          if (!is_valid) {
            // Token invalid → stay on page
            if (form_submitted) {
              navigate("/form-submission-success", { state: { openedViaLogin: true } });
            }
            else {
              console.log("Token invalid or expired");
              navigate("/unauth", { state: { openedViaButton: false } });
            }

          }
        } catch (error) {
          console.error("Error verifying token:", error);
          navigate("/unauth", { state: { openedViaButton: false } });
          // Optional: navigate to error page
        }
        finally {
          setIsLoading(false);
        }
      };
      verifyToken();
    }

  }, [location, navigate]);

  const handleOTPChange = (value, index) => {
    if (!/^[0-9]?$/.test(value)) return; // prevent non-number input

    const updated = [...otp];
    updated[index] = value;
    setOtp(updated);

    // Move to next input automatically
    if (value && index < 5) {
      document.getElementById(`otp-${index + 1}`).focus();
    }
  };

  const handleOTPPaste = (e) => {
    const paste = e.clipboardData.getData("text");

    if (!/^[0-9]{6}$/.test(paste)) return; // Must be exactly 6 digits

    const updated = paste.split("");
    setOtp(updated);

    // Move focus to last field
    document.getElementById("otp-5").focus();
  };


  const LogoGroup = ({ logos }) => (
    <div className="flex items-center gap-4 mb-8">
      {logos.map((logo, index) => (
        <img
          key={index}
          src={logo.src}
          onClick={() => window.open(logo.link, "_blank")}
          alt={logo.alt || `Logo ${index + 1}`}
          loading="eager"
          className="w-50 h-28 rounded-lg bg-white p-2"
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
      ))}
    </div>
  );

  const logos = [
    { src: Logo, alt: "Main Logo", link: "https://www.retinaspecialty.com/" },
    { src: segLogo, alt: "Secondary Logo", link: "https://southerneyegroup.com/" },
  ];

  const handleSendCode = async () => {
    try {
      setIsLoading(true);
      const response = await axios.post(
        `${config.API_BASE_URL}/send_otp`, // Replace with your Lambda endpoint
        { token: config.token }
      );

      if (response.status !== 200) {
        // Token invalid → stay on page
        console.log("Token invalid or expired");
        navigate("/unauth", { state: { openedViaButton: false } });
      } else {
        // Token valid → show OTP input
        setShowOTP(true);
      }
    } catch (error) {
      console.error("Error sending OTP:", error);
      navigate("/unauth", { state: { openedViaButton: false } });
      // Optional: navigate to error page
    } finally {
      setIsLoading(false);
    }
  }


  const handleLogin = async () => {
    try {
      setIsLoading(true);
      const response = await axios.post(
        `${config.API_BASE_URL}/verify_otp`, // Replace with your Lambda endpoint
        { token: config.token, otp: otp.join(""), dob }
      );

      if (response.status !== 200) {
        // Token invalid → stay on page
        console.log("Token invalid or expired");
        navigate("/unauth", { state: { openedViaButton: false } });
      } else {
        // Token valid → show OTP input
        config.accessToken = response.data.auth_token;
        navigate("/process");
      }
    } catch (error) {
      console.error("Error sending OTP:", error);
      navigate("/unauth", { state: { openedViaButton: false } });
      // Optional: navigate to error page
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="relative min-h-screen w-full overflow-hidden text-gray-900">
      {isLoading && (
        <NextBtnLoader />
      )}
      {/* Animated Background */}
      <Background />

      <div className="flex flex-col md:flex-row w-full items-start justify-center gap-10 py-12">

        {/* -------- LEFT CONTENT -------- */}
        <div className="flex flex-col items-center justify-center w-full md:w-1/2 z-10">

          {/* Hero Image */}
          <div
            className="relative w-full h-[350px] max-w-lg bg-cover bg-no-repeat bg-center rounded-xl overflow-hidden mb-8 md:mb-0 md:ml-8 z-10"
            style={{ backgroundImage: `url(${HeroImage})` }}
          >
            <div className="absolute inset-0 bg-white opacity-10 rounded-xl" />
          </div>

          {/* Content */}
          <div className="flex flex-col items-center justify-center text-center p-8 z-10">
            <LogoGroup logos={logos} />

            <h1 className="text-2xl md:text-4xl font-bold mb-4 text-black">
              Patient Intake Forms
            </h1>

            {!showOTP ? (
              <button
                onClick={handleSendCode}
                disabled={isLoading}
                className={`bg-sky-600 text-white py-3 px-6 rounded-xl shadow-lg 
            hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-600 
            transition-all duration-300 ease-in-out text-sm md:text-base 
            flex items-center gap-2
            ${isLoading ? 'opacity-75 cursor-not-allowed' : ''}`}
              >
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    <span>Connecting...</span>
                  </>
                ) : (
                  <>
                    <i className="fa-regular fa-paper-plane"></i>
                    <span>Send Code</span>
                  </>
                )}
              </button>
            ) : (
              <div className="flex flex-col items-center w-full mt-4">

                {/* OTP FIELD GROUP */}
                <div className="flex flex-col items-center mb-6 w-full">
                  <label
                    htmlFor="otp-0"
                    className="mb-2 text-sm font-medium text-gray-700"
                  >
                    Enter OTP
                  </label>

                  <div className="flex justify-center gap-2">
                    {otp.map((digit, index) => (
                      <input
                        key={index}
                        id={`otp-${index}`}
                        type="tel"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength="1"
                        value={digit}
                        onChange={(e) => handleOTPChange(e.target.value, index)}
                        onPaste={handleOTPPaste}
                        className="w-10 h-10 text-center text-xl font-semibold border border-sky-500 rounded-md
            focus:ring-2 focus:ring-sky-500 outline-none tracking-widest"
                      />
                    ))}
                  </div>
                </div>

                {/* DOB FIELD GROUP */}
                <div className="flex flex-col items-center mb-6 w-full max-w-xs">
                  <label
                    htmlFor="dob"
                    className="mb-2 text-sm font-medium text-gray-700"
                  >
                    Enter Date of Birth
                  </label>
                  <DOBPickerInput
                    id="dob"
                    label=""
                    value={dob}
                    onChange={(date) => {
                      if (!date) {
                        setDob(null);
                        return;
                      }

                      // Extract local date parts (NO timezone)
                      const year = date.getFullYear();
                      const month = String(date.getMonth() + 1).padStart(2, "0");
                      const day = String(date.getDate()).padStart(2, "0");

                      // Format as YYYY-MM-DD
                      const cleanDate = `${year}-${month}-${day}`;

                      setDob(cleanDate); // send clean date to backend
                    }}
                    disabled={isLoading}
                    required
                  />
                </div>

                {/* Button */}
                <button
                  onClick={handleLogin}
                  disabled={isLoading}
                  className={`bg-sky-600 text-white py-3 px-6 rounded-xl shadow-lg
      hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-600
      transition-all duration-300 ease-in-out text-sm md:text-base
      flex items-center gap-2
      ${isLoading ? "opacity-75 cursor-not-allowed" : ""}`}
                >
                  {isLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      <span>Connecting...</span>
                    </>
                  ) : (
                    <>
                      <i className="fa fa-sign-in"></i>
                      <span>Access Form</span>
                    </>
                  )}
                </button>

              </div>

            )}

            <p className="text-lg md:text-xl mt-8 mb-8 max-w-lg">
              Powered By Clear Healthcare
            </p>
          </div>
        </div>

        {/* -------- MARKET (Small Top Block or Side Block) -------- */}
        <div className="w-full md:w-auto">
          <Market />
        </div>
      </div>
    </div>


  );
};

export default LoginPage;
